import { useState, useRef, useEffect } from "react";
import { classNames } from "primereact/utils";
import styles from "./DefaultMultiSelectDropdown.module.scss";

export interface IMultiSelectOption {
  id: string | number;
  name: string;
  [key: string]: any;
}

interface IDefaultMultiSelectDropdownProps {
  options: IMultiSelectOption[];
  selectedOptions: IMultiSelectOption[];
  onSelectionChange: (selectedOptions: IMultiSelectOption[]) => void;
  placeholder?: string;
  className?: string;
  maxWidth?: number | string;
  disabled?: boolean;
  maxDisplayCount?: number;
  // When provided, renders a virtual "All" entry at the top of the list. Picking
  // it clears any specific selections; picking a specific entry while "All" is
  // active replaces "All" with that entry. Deselecting everything reverts to "All".
  selectAllOption?: IMultiSelectOption;
}

export const DefaultMultiSelectDropdown = ({
  options,
  selectedOptions,
  onSelectionChange,
  placeholder = "Изберете опции...",
  className,
  maxWidth,
  disabled = false,
  maxDisplayCount = 2,
  selectAllOption,
}: IDefaultMultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSelectAllActive =
    !!selectAllOption &&
    selectedOptions.some((item) => item.id === selectAllOption.id);

  const handleOptionToggle = (option: IMultiSelectOption) => {
    // Picking the "All" sentinel — switch to all-selected (only the sentinel).
    if (selectAllOption && option.id === selectAllOption.id) {
      onSelectionChange([selectAllOption]);
      return;
    }

    let newSelectedOptions: IMultiSelectOption[];

    if (isSelectAllActive) {
      // "All" was active and the user picked a specific entry → replace.
      newSelectedOptions = [option];
    } else {
      const isSelected = selectedOptions.some((item) => item.id === option.id);
      if (isSelected) {
        newSelectedOptions = selectedOptions.filter(
          (item) => item.id !== option.id
        );
      } else {
        newSelectedOptions = [...selectedOptions, option];
      }
    }

    if (selectAllOption && newSelectedOptions.length === 0) {
      newSelectedOptions = [selectAllOption];
    }

    onSelectionChange(newSelectedOptions);
  };

  const getDisplayText = () => {
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length <= maxDisplayCount) {
      return selectedOptions.map((item) => item.name).join(", ");
    }
    return `${selectedOptions.length} избрани`;
  };

  const renderedOptions = selectAllOption
    ? [selectAllOption, ...options]
    : options;

  return (
    <div
      className={classNames(styles.container, className)}
      ref={dropdownRef}
      style={{ maxWidth }}
    >
      <div
        className={classNames(styles.trigger, {
          [styles.active]: isOpen,
          [styles.disabled]: disabled,
        })}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span
          className={
            selectedOptions.length === 0 ? styles.placeholder : styles.value
          }
        >
          {getDisplayText()}
        </span>
        <span className={classNames(styles.arrow, { [styles.open]: isOpen })}>
          ▼
        </span>
      </div>

      {isOpen && !disabled && (
        <div className={styles.dropdown}>
          {renderedOptions.map((option) => {
            const isSelected = selectedOptions.some(
              (item) => item.id === option.id
            );
            return (
              <div
                key={option.id}
                className={classNames(styles.option, {
                  [styles.selected]: isSelected,
                })}
                onClick={() => handleOptionToggle(option)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  className={styles.checkbox}
                />
                {option.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
